
import { Package, BarChart3, Users, Cog, ChevronDown, Plus, List, Settings, Search, Scan, TrendingUp, Truck, Clock, Calendar, Building, FileText, CreditCard, Upload, AlertTriangle, Archive, Shield, ClipboardCheck, Brain, History } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

interface AdminNavigationProps {
  onNewOrder: () => void;
  onViewBackend: () => void;
  onPriceCheck: () => void;
  onScannerTest: () => void;
  onEmployeeManagement: () => void;
  onEmployeeClockIn: () => void;
  onSuppliersManagement: () => void;
  onCreditReports: () => void;
  onCostMonitor: () => void;
  onMasterProducts: () => void;
  onArchivedOrders?: () => void;
  onLoginAttempts: () => void;
}

export const AdminNavigation = ({
  onNewOrder,
  onViewBackend,
  onPriceCheck,
  onScannerTest,
  onEmployeeManagement,
  onEmployeeClockIn,
  onSuppliersManagement,
  onCreditReports,
  onCostMonitor,
  onMasterProducts,
  onArchivedOrders,
  onLoginAttempts
}: AdminNavigationProps) => {
  const navigate = useNavigate();

  return (
    <nav className="hidden md:flex items-center space-x-4">
      {/* Orders Menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="flex items-center space-x-1">
            <Package className="h-4 w-4" />
            <span>Orders</span>
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56 bg-white shadow-lg z-50">
          <DropdownMenuLabel>Order Management</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onNewOrder} className="flex items-center space-x-2">
            <Plus className="h-4 w-4" />
            <span>New Order Check-in</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onViewBackend} className="flex items-center space-x-2">
            <List className="h-4 w-4" />
            <span>View All Orders</span>
          </DropdownMenuItem>
          {onArchivedOrders && (
            <DropdownMenuItem onClick={onArchivedOrders} className="flex items-center space-x-2">
              <Archive className="h-4 w-4" />
              <span>Archived Orders</span>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem disabled className="flex items-center space-x-2">
            <Settings className="h-4 w-4" />
            <span>Order Workstation (Coming Soon)</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Inventory Menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="flex items-center space-x-1">
            <BarChart3 className="h-4 w-4" />
            <span>Inventory</span>
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56 bg-white shadow-lg z-50">
          <DropdownMenuLabel>Inventory & Pricing</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onPriceCheck} className="flex items-center space-x-2">
            <Search className="h-4 w-4" />
            <span>Price Check</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onScannerTest} className="flex items-center space-x-2">
            <Scan className="h-4 w-4" />
            <span>Scanner Test Lab</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onCostMonitor} className="flex items-center space-x-2">
            <TrendingUp className="h-4 w-4" />
            <span>Cost Monitor</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onMasterProducts} className="flex items-center space-x-2">
            <Package className="h-4 w-4" />
            <span>Master Products</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/edits-awaiting-approval')} className="flex items-center space-x-2">
            <Clock className="h-4 w-4" />
            <span>Edits Awaiting Approval</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/sync-out')} className="flex items-center space-x-2">
            <Upload className="h-4 w-4" />
            <span>Sync Out to Square</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/admin/pos-missing-products')} className="flex items-center space-x-2">
            <AlertTriangle className="h-4 w-4" />
            <span>Products Not Scanning</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Suppliers Menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="flex items-center space-x-1">
            <Truck className="h-4 w-4" />
            <span>Suppliers</span>
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56 bg-white shadow-lg z-50">
          <DropdownMenuLabel>Supplier Management</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate('/suppliers')} className="flex items-center space-x-2">
            <Building className="h-4 w-4" />
            <span>Manage Suppliers</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onCreditReports} className="flex items-center space-x-2">
            <CreditCard className="h-4 w-4" />
            <span>Credit Reports</span>
          </DropdownMenuItem>
          <DropdownMenuItem disabled className="flex items-center space-x-2">
            <FileText className="h-4 w-4" />
            <span>Supplier Analytics (Coming Soon)</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Staff Menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="flex items-center space-x-1">
            <Users className="h-4 w-4" />
            <span>Staff</span>
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56 bg-white shadow-lg z-50">
          <DropdownMenuLabel>Staff Management</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onEmployeeManagement} className="flex items-center space-x-2">
            <Users className="h-4 w-4" />
            <span>Employee Management</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onEmployeeClockIn} className="flex items-center space-x-2">
            <Clock className="h-4 w-4" />
            <span>Employee Clock-in</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/admin/compliance')} className="flex items-center space-x-2">
            <ClipboardCheck className="h-4 w-4" />
            <span>Compliance Dashboard</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/compliance-assistant')} className="flex items-center space-x-2">
            <Brain className="h-4 w-4" />
            <span>AI Compliance Assistant</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/hr-executive')} className="flex items-center space-x-2">
            <TrendingUp className="h-4 w-4" />
            <span>HR Executive Dashboard</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/audit-trail')} className="flex items-center space-x-2">
            <History className="h-4 w-4" />
            <span>Audit Trail</span>
          </DropdownMenuItem>
          <DropdownMenuItem disabled className="flex items-center space-x-2">
            <Calendar className="h-4 w-4" />
            <span>Employee Time Calendar (Coming Soon)</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* System Menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="flex items-center space-x-1">
            <Cog className="h-4 w-4" />
            <span>System</span>
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56 bg-white shadow-lg z-50">
          <DropdownMenuLabel>System & Reports</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onViewBackend} className="flex items-center space-x-2">
            <Settings className="h-4 w-4" />
            <span>Backend Dashboard</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onLoginAttempts} className="flex items-center space-x-2">
            <Shield className="h-4 w-4" />
            <span>Login Attempts</span>
          </DropdownMenuItem>
          <DropdownMenuItem disabled className="flex items-center space-x-2">
            <Package className="h-4 w-4" />
            <span>Product Workstation (Coming Soon)</span>
          </DropdownMenuItem>
          <DropdownMenuItem disabled className="flex items-center space-x-2">
            <Cog className="h-4 w-4" />
            <span>System Settings (Coming Soon)</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </nav>
  );
};
